import { Component, OnInit } from '@angular/core';
import {
  ProductPricingGroup,
  ProductsService,
} from 'src/app/services/products.service';
import {
  CASS_API_PRICING_CODES,
  CASS_API_VOLUME_BY_CODE,
  DATA_RETENTION_PRODUCT_CODE,
  DATA_RETENTION_VARIANTS,
  DESKTOP_PRODUCT_SORT_ORDER,
  TOOLKIT_EVALUATION_FEE_BY_CODE,
} from './pricing-display-order.config';
import {
  STATIC_PRICING_TAB_BY_ID,
  STATIC_TAB_ORDER,
} from './pricing-display-static.config';
import {
  StaticPricingSection,
  StaticPricingTab,
} from './pricing-display-static.types';

export type DesktopPricingSection = {
  title: string;
  requiresParent: boolean;
  useSitePrice: boolean;
};

export type CassApiPricingRow = {
  code: string;
  title: string;
  varianceCode: string;
  price12Month?: number | null;
  price24Month?: number | null;
  price36Month?: number | null;
  volume: number | null;
  perUnit: string;
};

export type DataRetentionPricingRow = {
  label: string;
  varianceCode: string;
  price12Month?: number | null;
  price24Month?: number | null;
  price36Month?: number | null;
};

export type ToolkitPricingRow = {
  code: string;
  title: string;
  evaluationFee: string;
  price12Month?: number | null;
  price24Month?: number | null;
  price36Month?: number | null;
};

const CASS_PRODUCT_TYPE = 'api';
const TOOLKIT_PRODUCT_TYPE = 'toolkit';

const DB_TAB_IDS = new Set(['desktop', 'api']);

@Component({
  selector: 'app-pricing-display',
  templateUrl: './pricing-display.component.html',
  styleUrl: './pricing-display.component.scss',
})
export class PricingDisplayComponent implements OnInit {
  priceLockLevelOptions = [
    { label: '0 (< 2007)', value: 0 },
    { label: '1 (2008 - 2013)', value: 1 },
    { label: '3 (2014 - 2025)', value: 3 },
    { label: '4 (2026)', value: 4 },
  ];

  priceLockLevel = 4;
  loading = false;
  error = '';

  groups: ProductPricingGroup[] = [];
  productTypes: string[] = [];
  allTabs: string[] = STATIC_TAB_ORDER;
  currentTab = '';

  readonly desktopSections: DesktopPricingSection[] = [
    {
      title: 'Original Licenses',
      requiresParent: false,
      useSitePrice: false,
    },
    {
      title: 'Additional Licenses',
      requiresParent: false,
      useSitePrice: true,
    },
    {
      title: 'Addon Licenses',
      requiresParent: true,
      useSitePrice: false,
    },
    {
      title: 'Addon Additional Licenses',
      requiresParent: true,
      useSitePrice: true,
    },
  ];

  constructor(private productsService: ProductsService) {}

  ngOnInit() {
    this.currentTab = this.allTabs[0] ?? '';
    this.loadPricing();
  }

  loadPricing() {
    this.loading = true;
    this.error = '';

    this.productsService.getProductPricing(this.priceLockLevel).subscribe({
      next: (res) => {
        this.loading = false;
        this.groups = res.groups ?? [];
        this.productTypes = this.sortProductTypes([
          ...new Set(this.groups.map((g) => g.product_type)),
        ]);
        if (!this.allTabs.includes(this.currentTab)) {
          this.currentTab = this.allTabs[0] ?? '';
        }
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.error?.error ?? 'Failed to load pricing';
        this.groups = [];
        this.productTypes = [];
        if (!this.allTabs.includes(this.currentTab)) {
          this.currentTab = this.allTabs[0] ?? '';
        }
      },
    });
  }

  switchTab(tabId: string) {
    this.currentTab = tabId;
  }

  isDbTab(tabId: string) {
    return DB_TAB_IDS.has(tabId);
  }

  staticTab(tabId: string): StaticPricingTab | undefined {
    return STATIC_PRICING_TAB_BY_ID.get(tabId);
  }

  get activeStaticTab(): StaticPricingTab | undefined {
    return this.staticTab(this.currentTab);
  }

  sectionHasTable(section: StaticPricingSection) {
    return section.rows.length > 0 || !!section.dynamicSource;
  }

  get cassApiRows(): CassApiPricingRow[] {
    const byCode = new Map(
      this.groups
        .filter((g) => g.product_type === CASS_PRODUCT_TYPE)
        .map((g) => [g.code, g])
    );

    return CASS_API_PRICING_CODES.map((code): CassApiPricingRow | null => {
      const group = byCode.get(code);
      if (!group) {
        return null;
      }
      const volume = CASS_API_VOLUME_BY_CODE[code] ?? null;
      return {
        code: group.code,
        title: group.title,
        varianceCode: group.variance_code,
        price12Month: group.price_12_month,
        price24Month: group.price_24_month,
        price36Month: group.price_36_month,
        volume,
        perUnit: this.cassPerUnit(group.price_12_month, volume),
      };
    }).filter((row): row is CassApiPricingRow => row !== null);
  }

  get dataRetentionRows(): DataRetentionPricingRow[] {
    const byVariance = new Map(
      this.groups
        .filter((g) => g.code === DATA_RETENTION_PRODUCT_CODE)
        .map((g) => [g.variance_code, g])
    );

    return DATA_RETENTION_VARIANTS.map(
      (variant): DataRetentionPricingRow | null => {
        const group = byVariance.get(variant.varianceCode);
        if (!group) {
          return null;
        }
        return {
          label: variant.label,
          varianceCode: group.variance_code,
          price12Month: group.price_12_month,
          price24Month: group.price_24_month,
          price36Month: group.price_36_month,
        };
      }
    ).filter((row): row is DataRetentionPricingRow => row !== null);
  }

  toolkitRows(codes: string[] = []): ToolkitPricingRow[] {
    const rows: ToolkitPricingRow[] = [];
    for (const code of codes) {
      const group = this.resolveToolkitGroup(code);
      console.log('toolkit group', group);
      if (!group) {
        continue;
      }
      rows.push({
        code: group.code,
        title: group.title,
        evaluationFee: TOOLKIT_EVALUATION_FEE_BY_CODE[code] ?? '',
        price12Month: group.price_12_month,
        price24Month: group.price_24_month,
        price36Month: group.price_36_month,
      });
      rows.push({
        code: group.code,
        title: `${group.title} - Additional Licenses`,
        evaluationFee: '',
        price12Month: group.site_price_12_month,
        price24Month: group.site_price_24_month,
        price36Month: group.site_price_36_month,
      });
    }
    return rows;
  }

  private resolveToolkitGroup(code: string) {
    const matches = this.groups.filter((g) => g.code === code);
    return (
      matches.find((g) => g.product_type === TOOLKIT_PRODUCT_TYPE) ?? matches[0]
    );
  }

  private cassPerUnit(price?: number | null, volume?: number | null) {
    if (price == null || !volume) {
      return '';
    }
    return (price / volume).toFixed(6);
  }

  paddedRow(section: StaticPricingSection, row: string[]) {
    const width = section.headers.length || row.length;
    const padded = [...row];
    while (padded.length < width) {
      padded.push('');
    }
    return padded.slice(0, width);
  }

  groupsForTab(productType: string) {
    const filtered = this.groups.filter((g) => g.product_type === productType);
    if (productType === 'desktop') {
      return this.sortDesktopGroups(filtered);
    }
    return filtered.sort((a, b) => a.code.localeCompare(b.code));
  }

  groupsForDesktopSection(section: DesktopPricingSection) {
    return this.groupsForTab('desktop').filter(
      (g) => g.requires_parent === section.requiresParent
    );
  }

  price12Month(group: ProductPricingGroup, useSitePrice: boolean) {
    return useSitePrice ? group.site_price_12_month : group.price_12_month;
  }

  price24Month(group: ProductPricingGroup, useSitePrice: boolean) {
    return useSitePrice ? group.site_price_24_month : group.price_24_month;
  }

  price36Month(group: ProductPricingGroup, useSitePrice: boolean) {
    return useSitePrice ? group.site_price_36_month : group.price_36_month;
  }

  tabLabel(tabId: string) {
    if (this.isDbTab(tabId)) {
      return tabId.charAt(0).toUpperCase() + tabId.slice(1);
    }
    return STATIC_PRICING_TAB_BY_ID.get(tabId)?.label ?? tabId;
  }

  private sortDesktopGroups(groups: ProductPricingGroup[]) {
    return [...groups].sort((a, b) => {
      const aIndex = DESKTOP_PRODUCT_SORT_ORDER.indexOf(a.code);
      const bIndex = DESKTOP_PRODUCT_SORT_ORDER.indexOf(b.code);
      const aKnown = aIndex !== -1;
      const bKnown = bIndex !== -1;

      if (aKnown && bKnown) {
        return aIndex - bIndex;
      }
      if (aKnown) {
        return -1;
      }
      if (bKnown) {
        return 1;
      }
      return a.code.localeCompare(b.code);
    });
  }

  private sortProductTypes(types: string[]) {
    const order = ['desktop', 'api'];
    return types.sort((a, b) => {
      const aIndex = order.indexOf(a);
      const bIndex = order.indexOf(b);
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) {
        return -1;
      }
      if (bIndex !== -1) {
        return 1;
      }
      return a.localeCompare(b);
    });
  }
}
